import {
  AdditiveBlending,
  BufferAttribute,
  CircleGeometry,
  BufferGeometry,
  Color,
  Group,
  Mesh,
  MeshBasicMaterial,
  Points,
  RingGeometry,
  ShaderMaterial,
  type Texture,
} from 'three'
import { Rng } from '@/core/Rng'

const PARTICLE_CAPACITY = 2200
const RING_POOL = 28
/** Concurrent ground sectors. Cone moves are on cooldowns; eight is plenty. */
const WEDGE_POOL = 8
/** Stroke widths, as a fraction of the radius, available to `ring`. */
const RING_STROKES = [0.06, 0.14, 0.28, 0.45]
/** Concurrent motion ribbons: six fighters plus a couple of spare emitters. */
const RIBBON_SLOTS = 8
/** Vertices along one ribbon. At 0.35 m apart this is about 6.6 m of history. */
const RIBBON_POINTS = 20
const RIBBON_STEP = 0.35
/** Height of the band above the slab — mid-body, where the eye follows. */
const RIBBON_Y = 0.85
/** Seconds without an update before a ribbon fades and frees its slot. */
const RIBBON_IDLE = 0.12
/** Where inactive particles wait: far below the void floor, out of every shot. */
const PARKED_Y = -400

/**
 * All transient visuals in one place, entirely pool-backed.
 *
 * Nothing here allocates during play: particles live in fixed typed arrays and
 * rings are recycled meshes. A brawler generates bursts of effects at exactly
 * the moments where a GC pause would be most noticeable, so the allocation-free
 * path is not premature optimisation — it is the whole point.
 */
export class Vfx {
  readonly group = new Group()

  private readonly positions: Float32Array
  private readonly colors: Float32Array
  private readonly sizes: Float32Array
  private readonly velocities: Float32Array
  private readonly life: Float32Array
  private readonly maxLife: Float32Array
  private readonly drag: Float32Array
  private readonly gravity: Float32Array
  private readonly baseColor: Float32Array
  private cursor = 0

  private readonly points: Points
  private readonly geometry: BufferGeometry
  private readonly ringGeometries: BufferGeometry[]
  private readonly rings: Mesh[] = []
  private readonly ringState: Array<{
    active: boolean
    t: number
    duration: number
    from: number
    to: number
    opacity: number
  }> = []

  private readonly wedges: Mesh[] = []
  private readonly wedgeState: Array<{
    active: boolean
    t: number
    duration: number
    radius: number
    opacity: number
  }> = []

  /**
   * Motion ribbons. Each slot owns one mesh whose geometry is rewritten from a
   * short history of ground positions; nothing is allocated after construction.
   */
  private readonly ribbons: Array<{
    active: boolean
    key: number
    /** Flat [x0, z0, x1, z1, …] history, oldest first. */
    points: Float32Array
    count: number
    idle: number
    fade: number
    width: number
    strength: number
    mesh: Mesh
    position: Float32Array
    alpha: Float32Array
  }> = []

  private readonly rng = new Rng(0xc0ffee)
  private ambientTimer = 0
  private readonly scratch = new Color()

  constructor(sprite: Texture) {
    this.positions = new Float32Array(PARTICLE_CAPACITY * 3)
    this.colors = new Float32Array(PARTICLE_CAPACITY * 3)
    this.baseColor = new Float32Array(PARTICLE_CAPACITY * 3)
    this.sizes = new Float32Array(PARTICLE_CAPACITY)
    this.velocities = new Float32Array(PARTICLE_CAPACITY * 3)
    this.life = new Float32Array(PARTICLE_CAPACITY)
    this.maxLife = new Float32Array(PARTICLE_CAPACITY)
    this.drag = new Float32Array(PARTICLE_CAPACITY)
    this.gravity = new Float32Array(PARTICLE_CAPACITY)

    // Dead particles are parked far below the world. Size 0 should be enough to
    // hide them, but some drivers clamp gl_PointSize to a minimum of one pixel,
    // which would paint a permanent bright dot at the arena's origin.
    for (let i = 0; i < PARTICLE_CAPACITY; i++) this.positions[i * 3 + 1] = PARKED_Y

    this.geometry = new BufferGeometry()
    this.geometry.setAttribute('position', new BufferAttribute(this.positions, 3))
    this.geometry.setAttribute('aColor', new BufferAttribute(this.colors, 3))
    this.geometry.setAttribute('aSize', new BufferAttribute(this.sizes, 1))
    this.geometry.setDrawRange(0, PARTICLE_CAPACITY)

    const material = new ShaderMaterial({
      uniforms: { uMap: { value: sprite } },
      transparent: true,
      depthWrite: false,
      blending: AdditiveBlending,
      vertexShader: /* glsl */ `
        attribute vec3 aColor;
        attribute float aSize;
        varying vec3 vColor;
        void main() {
          vColor = aColor;
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          // Dead particles get size 0 and collapse to a degenerate point.
          gl_PointSize = aSize * (420.0 / max(-mv.z, 0.001));
          gl_Position = projectionMatrix * mv;
        }
      `,
      fragmentShader: /* glsl */ `
        uniform sampler2D uMap;
        varying vec3 vColor;
        void main() {
          float a = texture2D(uMap, gl_PointCoord).a;
          if (a < 0.01) discard;
          gl_FragColor = vec4(vColor * a, a);
        }
      `,
    })

    this.points = new Points(this.geometry, material)
    this.points.frustumCulled = false
    this.group.add(this.points)

    this.ringGeometries = RING_STROKES.map((stroke) => new RingGeometry(1 - stroke, 1, 64))
    for (let i = 0; i < RING_POOL; i++) {
      const mesh = new Mesh(
        this.ringGeometries[1],
        new MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0,
          blending: AdditiveBlending,
          depthWrite: false,
          side: 2,
          toneMapped: false,
        }),
      )
      mesh.rotation.x = -Math.PI / 2
      mesh.visible = false
      this.group.add(mesh)
      this.rings.push(mesh)
      this.ringState.push({
        active: false,
        t: 0,
        duration: 1,
        from: 0,
        to: 1,
        opacity: 1,
      })
    }

    // Wedges: the shape half the personal moves actually have.
    //
    // The vocabulary was rings (a circle) and cones (a spray of particles), and
    // neither is a sector. A cone move drawn as thirty specks is invisible from
    // this camera — measured: 34 particles of 0.3 m over a 6.5 m fan is about
    // five pixels each, spread across a hundred — and drawn as a ring it claims
    // to hit behind the caster. A filled sector is the hitbox, at the size of
    // the hitbox, which is the only honest thing to put on screen.
    for (let i = 0; i < WEDGE_POOL; i++) {
      const mesh = new Mesh(
        new CircleGeometry(1, 24, 0, 1),
        new MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0,
          blending: AdditiveBlending,
          depthWrite: false,
          side: 2,
          toneMapped: false,
        }),
      )
      mesh.rotation.x = -Math.PI / 2
      mesh.visible = false
      this.group.add(mesh)
      this.wedges.push(mesh)
      this.wedgeState.push({ active: false, t: 0, duration: 1, radius: 1, opacity: 1 })
    }

    this.buildRibbons()
  }

  /**
   * A ground sector sweeping out along a facing — the shape of a cone attack.
   *
   * `halfAngle` and `radius` come from the move's own hitbox, so the drawing can
   * never promise reach the move does not have.
   */
  wedge(
    x: number,
    z: number,
    angle: number,
    halfAngle: number,
    radius: number,
    color: number,
    duration = 0.35,
    opacity = 0.5,
  ): void {
    const index = this.wedgeState.findIndex((w) => !w.active)
    if (index === -1) return
    const state = this.wedgeState[index]
    state.active = true
    state.t = 0
    state.duration = duration
    state.radius = radius
    state.opacity = opacity

    const mesh = this.wedges[index]
    mesh.geometry.dispose()
    // Built around zero and rotated into place, so the sector is symmetric about
    // the facing rather than starting at it.
    mesh.geometry = new CircleGeometry(1, 24, -halfAngle, halfAngle * 2)
    mesh.visible = true
    mesh.position.set(x, 0.08, z)
    // The circle is built in the XY plane and laid flat, which mirrors its
    // winding: the yaw that lines it up with a world facing is −angle, not angle.
    mesh.rotation.set(-Math.PI / 2, 0, -angle + Math.PI / 2)
    mesh.scale.setScalar(0.35)
    ;(mesh.material as MeshBasicMaterial).color.setHex(color)
  }

  /**
   * One mesh per ribbon slot: a flat band of `RIBBON_POINTS − 1` quads lying
   * just above the slab. The index buffer never changes, only the positions and
   * the per-vertex alpha, so a ribbon costs one buffer upload per frame.
   */
  private buildRibbons(): void {
    const quads = RIBBON_POINTS - 1
    const index = new Uint16Array(quads * 6)
    for (let q = 0; q < quads; q++) {
      const a = q * 2
      index.set([a, a + 1, a + 2, a + 1, a + 3, a + 2], q * 6)
    }

    for (let i = 0; i < RIBBON_SLOTS; i++) {
      const position = new Float32Array(RIBBON_POINTS * 2 * 3)
      const alpha = new Float32Array(RIBBON_POINTS * 2)
      const geometry = new BufferGeometry()
      geometry.setAttribute('position', new BufferAttribute(position, 3))
      geometry.setAttribute('aAlpha', new BufferAttribute(alpha, 1))
      geometry.setIndex(new BufferAttribute(index, 1))

      const material = new MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        depthWrite: false,
        blending: AdditiveBlending,
        side: 2,
        toneMapped: false,
      })
      // Per-vertex alpha through a two-line patch on the standard material:
      // the tail has to disappear into nothing, and a uniform opacity would
      // give a band that simply stops, which reads as a stick, not a streak.
      material.onBeforeCompile = (shader) => {
        shader.vertexShader = shader.vertexShader
          .replace('#include <common>', '#include <common>\nattribute float aAlpha;\nvarying float vAlpha;')
          .replace('#include <begin_vertex>', '#include <begin_vertex>\nvAlpha = aAlpha;')
        shader.fragmentShader = shader.fragmentShader
          .replace('#include <common>', '#include <common>\nvarying float vAlpha;')
          .replace(
            '#include <opaque_fragment>',
            'gl_FragColor = vec4( outgoingLight * vAlpha, diffuseColor.a * vAlpha );',
          )
      }

      const mesh = new Mesh(geometry, material)
      mesh.frustumCulled = false
      mesh.visible = false
      this.group.add(mesh)

      this.ribbons.push({
        active: false,
        key: -1,
        points: new Float32Array(RIBBON_POINTS * 2),
        count: 0,
        idle: 0,
        fade: 1,
        width: 0.55,
        strength: 1,
        mesh,
        position,
        alpha,
      })
    }
  }

  // ────────────────────────────────────────────────────────────── emitters ──

  /**
   * Impact spray: sparks along the knockback direction plus a scuff of dust.
   *
   * Every quantity here is scaled against a 40 m/s launch rather than the ~16
   * the old curves assumed. Under those, a graze and the hardest hit in the
   * game produced the same 40 particles and the same ring: the channel was
   * saturated before the interesting range began, so nothing a player did
   * changed what they saw. See docs/PHYSICS.md §6.4.
   */
  hit(x: number, z: number, dirX: number, dirZ: number, power: number, color: number): void {
    const t = Math.min(1, power / 40)
    const count = Math.min(120, 8 + Math.floor(power * 2.6))
    // A ring at the point of contact gives the hit a readable centre. It grows
    // from 2 m to 8 m across the range, so the size of the hit is legible from
    // the ring alone, with a stroke that thickens with the blow.
    this.ring(x, z, 0.4, 2 + t * 6, color, 0.26 + t * 0.2, 0.9, 0.1 + t * 0.22)
    this.scratch.setHex(color)
    for (let i = 0; i < count; i++) {
      const spread = this.rng.spread(0.7)
      const dx = dirX * Math.cos(spread) - dirZ * Math.sin(spread)
      const dz = dirX * Math.sin(spread) + dirZ * Math.cos(spread)
      const speed = this.rng.range(2.5, 5 + power * 0.55)
      this.spawn(
        x + dx * 0.2,
        this.rng.range(0.5, 1.2),
        z + dz * 0.2,
        dx * speed,
        this.rng.range(1.5, 5),
        dz * speed,
        this.rng.range(0.24, 0.55),
        this.rng.range(0.3, 0.62) * (0.7 + t * 0.6),
        this.scratch.r * 2.4,
        this.scratch.g * 2.4,
        this.scratch.b * 2.4,
        2.5,
        11,
      )
    }
    this.dust(x, z, 8 + Math.floor(t * 18), 0.6 + t)
  }

  /**
   * One skip of a launched fighter off the slab.
   *
   * The bounces are how the strength of a hit is read after the first frame:
   * three contacts mean three puffs and three scrapes, and a weak hit has one.
   */
  skip(x: number, z: number, speed: number, color: number, index: number): void {
    const t = Math.min(1, speed / 40)
    this.ring(x, z, 0.3, 1.4 + t * 3, color, 0.22, 0.5 / index, 0.08 + t * 0.1)
    this.dust(x, z, 10 + Math.floor(t * 14), 0.8 + t * 0.8)
    this.scratch.setHex(color)
    for (let i = 0; i < 8 + Math.floor(t * 14); i++) {
      const a = this.rng.angle()
      const s = this.rng.range(1.5, 3 + speed * 0.2)
      this.spawn(
        x,
        0.12,
        z,
        Math.sin(a) * s,
        this.rng.range(1, 3.5),
        Math.cos(a) * s,
        this.rng.range(0.18, 0.4),
        this.rng.range(0.22, 0.44),
        this.scratch.r * 2,
        this.scratch.g * 2,
        this.scratch.b * 2,
        3,
        10,
      )
    }
  }

  /**
   * A cast that connected with nothing.
   *
   * Deliberately the quietest thing in this file: before the rewrite a whiff
   * threw a full ring and a cone of debris, so missing looked more impressive
   * than hitting and the screen said nothing about which had happened.
   */
  whiff(x: number, z: number, angle: number, color: number): void {
    this.scratch.setHex(color)
    for (let i = 0; i < 7; i++) {
      const a = angle + this.rng.spread(0.5)
      const s = this.rng.range(1.5, 3.5)
      this.spawn(
        x + Math.sin(a) * 0.5,
        this.rng.range(0.4, 1),
        z + Math.cos(a) * 0.5,
        Math.sin(a) * s,
        this.rng.range(0.3, 1.2),
        Math.cos(a) * s,
        this.rng.range(0.14, 0.26),
        this.rng.range(0.14, 0.24),
        this.scratch.r * 0.9,
        this.scratch.g * 0.9,
        this.scratch.b * 0.9,
        5,
        3,
      )
    }
    this.dust(x, z, 5, 0.5)
  }

  /** A short bright scatter — the bolt kissing the floor or a wall. */
  spark(x: number, z: number, color: number, count = 10): void {
    this.scratch.setHex(color)
    for (let i = 0; i < count; i++) {
      const a = this.rng.angle()
      const s = this.rng.range(1.5, 5)
      this.spawn(
        x,
        this.rng.range(0.15, 0.7),
        z,
        Math.sin(a) * s,
        this.rng.range(1, 4),
        Math.cos(a) * s,
        this.rng.range(0.12, 0.3),
        this.rng.range(0.2, 0.4),
        this.scratch.r * 3,
        this.scratch.g * 3,
        this.scratch.b * 3,
        3.2,
        9,
      )
    }
  }

  /**
   * A wedge of debris thrown along a facing direction — the visual signature of
   * a melee shove. Without it a Push reads as nothing happening.
   */
  cone(x: number, z: number, angle: number, range: number, color: number, count = 24): void {
    this.scratch.setHex(color)
    const halfArc = 0.85
    for (let i = 0; i < count; i++) {
      const a = angle + this.rng.spread(halfArc)
      const speed = this.rng.range(5, 11)
      const start = this.rng.range(0.3, range * 0.5)
      this.spawn(
        x + Math.sin(a) * start,
        this.rng.range(0.35, 1.35),
        z + Math.cos(a) * start,
        Math.sin(a) * speed,
        this.rng.range(0.8, 3.4),
        Math.cos(a) * speed,
        this.rng.range(0.22, 0.42),
        this.rng.range(0.14, 0.3),
        this.scratch.r * 2.6,
        this.scratch.g * 2.6,
        this.scratch.b * 2.6,
        4.2,
        6,
      )
    }
  }

  /**
   * A streak left behind a moving fighter — a real ribbon along the path.
   *
   * This used to be two particles per frame, and it did not work: on this
   * camera the arena is about 16 pixels to the metre, so a 0.4 m spark is six
   * pixels, and they were spawned *on* the fighter with only −0.12× of their
   * velocity, so they hung on the body instead of laying down a path. Thirty
   * six-pixel specks stuck to a 32-pixel figure is not a trail at any count,
   * which is why "add more particles" was never going to answer the complaint.
   *
   * A ribbon shows the *history* of the motion, which is the thing that reads
   * as speed. Call it every frame for as long as the emitter should have one;
   * a slot that stops being fed fades out and frees itself.
   */
  trail(
    key: number,
    x: number,
    z: number,
    color: number,
    width = 0.55,
    strength = 1,
  ): void {
    let slot = this.ribbons.find((r) => r.active && r.key === key)
    if (!slot) {
      slot = this.ribbons.find((r) => !r.active)
      if (!slot) return
      slot.active = true
      slot.key = key
      slot.count = 0
      slot.fade = 1
    }

    slot.idle = 0
    slot.width = width
    slot.strength = strength
    ;(slot.mesh.material as MeshBasicMaterial).color.setHex(color)

    // The last point is a cursor that rides the emitter every frame; the one
    // before it is the newest committed vertex. A new vertex is committed only
    // once the cursor has pulled RIBBON_STEP away from it, so standing still
    // does not eat the history and walking does not spend one per frame.
    //
    // Measuring against the cursor instead of against the committed point is
    // exactly what broke the first attempt: the cursor moves with the fighter,
    // so the distance to it was always one frame of travel, the test never
    // passed, and the ribbon spent its whole life one vertex long.
    if (slot.count === 0) {
      slot.points[0] = x
      slot.points[1] = z
      slot.points[2] = x
      slot.points[3] = z
      slot.count = 2
      return
    }

    slot.points[(slot.count - 1) * 2] = x
    slot.points[(slot.count - 1) * 2 + 1] = z

    const anchor = slot.count - 2
    const ax = slot.points[anchor * 2]
    const az = slot.points[anchor * 2 + 1]
    if (Math.hypot(x - ax, z - az) < RIBBON_STEP) return

    if (slot.count === RIBBON_POINTS) {
      slot.points.copyWithin(0, 2)
      slot.count--
    }
    slot.points[slot.count * 2] = x
    slot.points[slot.count * 2 + 1] = z
    slot.count++
  }

  /** Grey puff kicked off the concrete. */
  dust(x: number, z: number, count = 12, spread = 1): void {
    for (let i = 0; i < count; i++) {
      const a = this.rng.angle()
      const r = this.rng.range(0, spread)
      this.spawn(
        x + Math.sin(a) * r,
        this.rng.range(0.05, 0.4),
        z + Math.cos(a) * r,
        Math.sin(a) * this.rng.range(0.6, 2),
        this.rng.range(0.4, 1.6),
        Math.cos(a) * this.rng.range(0.6, 2),
        this.rng.range(0.5, 1.1),
        this.rng.range(0.25, 0.55),
        0.32,
        0.31,
        0.27,
        1.6,
        -0.4,
      )
    }
  }

  explosion(x: number, z: number, radius: number, color = 0xffa23c): void {
    this.scratch.setHex(color)
    for (let i = 0; i < 60; i++) {
      const a = this.rng.angle()
      const speed = this.rng.range(3, 9)
      this.spawn(
        x,
        this.rng.range(0.2, 1.4),
        z,
        Math.sin(a) * speed,
        this.rng.range(2, 8),
        Math.cos(a) * speed,
        this.rng.range(0.35, 0.9),
        this.rng.range(0.18, 0.42),
        this.scratch.r * 2.6,
        this.scratch.g * 2.2,
        this.scratch.b * 1.4,
        1.9,
        9,
      )
    }
    this.dust(x, z, 26, radius * 0.4)
    this.ring(x, z, 0.6, radius, color, 0.45, 1)
  }

  /**
   * Expanding ground ring — waves, explosions, vortex collapse, impacts.
   *
   * `thickness` is the stroke as a fraction of the radius; scale it with the
   * force behind the ring, because a hairline circle at eight metres is less
   * visible than a fat one at three and would undo the size scaling.
   */
  ring(
    x: number,
    z: number,
    from: number,
    to: number,
    color: number,
    duration: number,
    opacity = 0.9,
    thickness = 0.14,
  ): void {
    const index = this.ringState.findIndex((r) => !r.active)
    if (index === -1) return
    const state = this.ringState[index]
    state.active = true
    state.t = 0
    state.duration = duration
    state.from = from
    state.to = to
    state.opacity = opacity
    const mesh = this.rings[index]
    mesh.geometry = this.ringGeometry(thickness)
    mesh.visible = true
    mesh.position.set(x, 0.09, z)
    mesh.scale.setScalar(from)
    ;(mesh.material as MeshBasicMaterial).color.setHex(color)
  }

  /** Nearest pre-built stroke width; the geometries are shared, never cloned. */
  private ringGeometry(thickness: number): BufferGeometry {
    let best = 0
    for (let i = 1; i < RING_STROKES.length; i++) {
      if (Math.abs(RING_STROKES[i] - thickness) < Math.abs(RING_STROKES[best] - thickness)) {
        best = i
      }
    }
    return this.ringGeometries[best]
  }

  /** Vertical column of embers — burner anomaly firing. */
  burst(x: number, z: number, radius: number, color = 0x8fff3c): void {
    this.scratch.setHex(color)
    for (let i = 0; i < 44; i++) {
      const a = this.rng.angle()
      const r = this.rng.range(0, radius * 0.7)
      this.spawn(
        x + Math.sin(a) * r,
        0.1,
        z + Math.cos(a) * r,
        Math.sin(a) * this.rng.range(0.5, 2.5),
        this.rng.range(6, 13),
        Math.cos(a) * this.rng.range(0.5, 2.5),
        this.rng.range(0.5, 1.1),
        this.rng.range(0.16, 0.36),
        this.scratch.r * 2.4,
        this.scratch.g * 2.4,
        this.scratch.b * 2.4,
        1.1,
        7,
      )
    }
    this.ring(x, z, 0.4, radius, color, 0.4, 0.85)
  }

  /** Slow motes of ash drifting through the whole scene. */
  private ambient(dt: number, extent: number): void {
    this.ambientTimer -= dt
    if (this.ambientTimer > 0) return
    this.ambientTimer = 0.06

    for (let i = 0; i < 2; i++) {
      const a = this.rng.angle()
      const r = this.rng.range(0, extent)
      this.spawn(
        Math.sin(a) * r,
        this.rng.range(-1, 6),
        Math.cos(a) * r,
        this.rng.spread(0.35),
        this.rng.range(0.1, 0.5),
        this.rng.spread(0.35),
        this.rng.range(3, 7),
        this.rng.range(0.05, 0.13),
        0.36,
        0.4,
        0.3,
        0.15,
        -0.05,
      )
    }
  }

  // ─────────────────────────────────────────────────────────────── internals ──

  private spawn(
    x: number,
    y: number,
    z: number,
    vx: number,
    vy: number,
    vz: number,
    life: number,
    size: number,
    r: number,
    g: number,
    b: number,
    drag: number,
    gravity: number,
  ): void {
    const i = this.cursor
    this.cursor = (this.cursor + 1) % PARTICLE_CAPACITY

    const p = i * 3
    this.positions[p] = x
    this.positions[p + 1] = y
    this.positions[p + 2] = z
    this.velocities[p] = vx
    this.velocities[p + 1] = vy
    this.velocities[p + 2] = vz
    this.baseColor[p] = r
    this.baseColor[p + 1] = g
    this.baseColor[p + 2] = b
    this.colors[p] = r
    this.colors[p + 1] = g
    this.colors[p + 2] = b
    this.sizes[i] = size
    this.life[i] = life
    this.maxLife[i] = life
    this.drag[i] = drag
    this.gravity[i] = gravity
  }

  /** Rewrites the geometry of every live ribbon and retires the stale ones. */
  private updateRibbons(dt: number): void {
    for (const slot of this.ribbons) {
      if (!slot.active) continue

      slot.idle += dt
      if (slot.idle > RIBBON_IDLE) {
        // Not being fed any more: shrink the tail away rather than blink out.
        slot.fade -= dt * 4
        if (slot.fade <= 0 || slot.count < 2) {
          slot.active = false
          slot.mesh.visible = false
          continue
        }
      }

      if (slot.count < 2) {
        slot.mesh.visible = false
        continue
      }

      for (let i = 0; i < slot.count; i++) {
        const x = slot.points[i * 2]
        const z = slot.points[i * 2 + 1]
        // Perpendicular to the local direction of travel, so the band lies
        // flat along the path and stays broadside to an overhead camera.
        const j = Math.min(i + 1, slot.count - 1)
        const k = Math.max(i - 1, 0)
        let dx = slot.points[j * 2] - slot.points[k * 2]
        let dz = slot.points[j * 2 + 1] - slot.points[k * 2 + 1]
        const len = Math.hypot(dx, dz) || 1
        dx /= len
        dz /= len

        // Head is full width, tail tapers to a point.
        const along = i / (slot.count - 1)
        const half = slot.width * (0.18 + 0.82 * along) * 0.5
        const v = i * 6
        slot.position[v] = x - dz * half
        slot.position[v + 1] = RIBBON_Y
        slot.position[v + 2] = z + dx * half
        slot.position[v + 3] = x + dz * half
        slot.position[v + 4] = RIBBON_Y
        slot.position[v + 5] = z - dx * half

        // Fades along the band rather than squared: a square falloff put the
        // brightness in the last metre only, so the ribbon read as a short
        // flare stuck to the fighter rather than as a length of travel.
        const a = along ** 1.3 * slot.fade * slot.strength
        slot.alpha[i * 2] = a
        slot.alpha[i * 2 + 1] = a
      }

      // Unused vertices collapse onto the tail instead of sitting at the origin.
      for (let i = slot.count; i < RIBBON_POINTS; i++) {
        const last = (slot.count - 1) * 6
        slot.position.copyWithin(i * 6, last, last + 6)
        slot.position.copyWithin(i * 6 + 3, last + 3, last + 6)
        slot.alpha[i * 2] = 0
        slot.alpha[i * 2 + 1] = 0
      }

      const geometry = slot.mesh.geometry
      ;(geometry.getAttribute('position') as BufferAttribute).needsUpdate = true
      ;(geometry.getAttribute('aAlpha') as BufferAttribute).needsUpdate = true
      slot.mesh.visible = true
    }
  }

  update(dt: number, extent: number): void {
    this.ambient(dt, extent)
    this.updateRibbons(dt)

    for (let i = 0; i < PARTICLE_CAPACITY; i++) {
      if (this.life[i] <= 0) {
        if (this.sizes[i] !== 0) {
          this.sizes[i] = 0
          this.positions[i * 3 + 1] = PARKED_Y
        }
        continue
      }

      this.life[i] -= dt
      const p = i * 3
      const decay = Math.exp(-this.drag[i] * dt)

      this.velocities[p] *= decay
      this.velocities[p + 1] = this.velocities[p + 1] * decay - this.gravity[i] * dt
      this.velocities[p + 2] *= decay

      this.positions[p] += this.velocities[p] * dt
      this.positions[p + 1] += this.velocities[p + 1] * dt
      this.positions[p + 2] += this.velocities[p + 2] * dt

      // Additive blending means fading the colour to black is the fade-out.
      const t = Math.max(0, this.life[i] / this.maxLife[i])
      const fade = t * t
      this.colors[p] = this.baseColor[p] * fade
      this.colors[p + 1] = this.baseColor[p + 1] * fade
      this.colors[p + 2] = this.baseColor[p + 2] * fade

      if (this.life[i] <= 0) this.sizes[i] = 0
    }

    ;(this.geometry.getAttribute('position') as BufferAttribute).needsUpdate = true
    ;(this.geometry.getAttribute('aColor') as BufferAttribute).needsUpdate = true
    ;(this.geometry.getAttribute('aSize') as BufferAttribute).needsUpdate = true

    for (let i = 0; i < this.rings.length; i++) {
      const state = this.ringState[i]
      if (!state.active) continue
      state.t += dt
      const k = Math.min(1, state.t / state.duration)
      const mesh = this.rings[i]
      const eased = 1 - Math.pow(1 - k, 3)
      mesh.scale.setScalar(state.from + (state.to - state.from) * eased)
      // Brightness is held until the ring has reached its full size and only
      // then cut. The old curve faded as it expanded, so the ring was at its
      // brightest while it was still a dot and invisible by the time it was
      // large enough to say anything about the size of the hit.
      ;(mesh.material as MeshBasicMaterial).opacity = state.opacity * Math.min(1, (1 - k) * 3.4)
      if (k >= 1) {
        state.active = false
        mesh.visible = false
      }
    }

    for (let i = 0; i < this.wedges.length; i++) {
      const state = this.wedgeState[i]
      if (!state.active) continue
      state.t += dt
      const k = Math.min(1, state.t / state.duration)
      const mesh = this.wedges[i]
      // Sweeps out fast and holds its full reach while it fades, so the frame
      // that lands in the player's eye is the one showing how far the move went.
      const eased = 1 - Math.pow(1 - k, 4)
      mesh.scale.setScalar(0.35 + (state.radius - 0.35) * eased)
      ;(mesh.material as MeshBasicMaterial).opacity =
        state.opacity * Math.min(1, (1 - k) * 2.2)
      if (k >= 1) {
        state.active = false
        mesh.visible = false
      }
    }
  }
}
